<template>
  <div class="bg-charcoal-950">
    <section
      class="bg-charcoal-950 transition-{right} duration-500 absolute inset-0 flex flex-col h-screen"
      :class="isPanelVisible ? 'sm:right-[350px]' : 'panel-hidden'"
    >

      <div class="z-20 relative flex items-center bg-zinc-950 p-1 dark">
        <h1 class="absolute left-1/2 -translate-x-1/2 text-white text-lg font-bold font-sans leading-none pointer-events-none select-none">
          Video Editor
        </h1>
        <core-tooltip text="Close">
          <core-selection-toolbar-button
            dark
            size="lg"
            icon="i-ri-arrow-left-line"
            @click="modal.close()"
          />
        </core-tooltip>

        <ul class="m-0 p-0 list-none flex items-center gap-2 flex-1 justify-end">
          <li v-if="!clips.length">
            <button
              type="button"
              class="h-8 px-4 rounded-md border border-zinc-600 bg-zinc-800 text-white text-sm font-medium transition-colors hover:bg-zinc-700"
              @click="createTitleCoverClip"
            >
              Create Title Cover
            </button>
          </li>
          <li>
            <core-tooltip text="Capture frame">
              <core-selection-toolbar-button
                dark
                size="lg"
                icon="i-ri-camera-line"
                @click="captureVideoFrame"
              />
            </core-tooltip>
          </li>
          <li v-if="clips.length">
            <core-tooltip text="All changes saved" placement="bottom">
              <button class="text-white mt-1 px-3">
                <u-icon name="i-mdi-cloud-check-variant" class="flex text-2xl"/>
              </button>
            </core-tooltip>
          </li>
          <li>
            <core-tooltip text="Suggest Improvement">
              <a
                href="https://forms.gle/9y3dYWQjWJyLjgSW7"
                target="_blank"
                class="no-underline"
              >
                <core-selection-toolbar-button
                  dark
                  size="lg"
                  icon="i-ri-lightbulb-line"
                />
              </a>
            </core-tooltip>
          </li>
          <li>
            <core-tooltip :text="isPanelVisible ? 'Hide' : 'Show'">
              <core-selection-toolbar-button
                dark
                size="lg"
                icon="i-ri-information-2-line"
                @click="togglePanel"
              />
            </core-tooltip>
          </li>
        </ul>
      </div>

      <!-- stage -->
      <div class="flex-1 m-4 relative bg-charcoal-950" ref="videoContainerEl">
        <div class="absolute-cover flex-center">
          <core-media-player
            :src="videoSrc"
            :name="file.name"
            :width="featurePosition.style.value.width"
            :height="featurePosition.style.value.height"
            is-active
            hide-overlay-play-btn
            hide-control-bar
            prevent-double-click
            ref="videoPlayerComp"
            @error="onVideoError"
          />
        </div>
      </div>

      <div class="justify-self-end m-4" v-if="player">
        <video-editor-timeline
          :player="player"
          :video-src="videoSrc"
          :disable-edit="disableClipEditing"
          :initial-duration-seconds="initialTimelineDurationSeconds"
          @current-time-change="setDisplayedCurrentTime"
        />
        <video-editor-tool-bar :player="player" :current-time-seconds="displayedCurrentTimeSeconds" />
      </div>

    </section>

    <section
      :class="[
        'absolute top-0 bottom-0 right-0 w-full xs:w-[350px] bg-gray-800 dark pt-3 px-3.5 z-20 duration-500 transition-transform flex flex-col',
        {
          'translate-x-full': !isPanelVisible
        }
      ]"
    >
      <div class="flex gap-2 items-center">
        <span class="text-md font-semibold flex-1 text-white">Segments to Export</span>
        <button
          type="button"
          class="inline-flex items-center gap-1 text-sm text-gray-400 font-medium transition-colors hover:text-gray-300"
          @click="toggleInfoSection"
        >
          <span>{{ isInfoSectionVisible ? 'Hide Info' : 'Show Info' }}</span>
          <u-icon
            name="i-ri-arrow-up-s-line"
            class="w-4 h-4 transition-transform duration-300"
            :class="{'rotate-180': !isInfoSectionVisible}"
          />
        </button>
      </div>

      <div
        class="overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-in-out"
        :class="isInfoSectionVisible ? 'max-h-48 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'"
      >
        <div class="border-b border-black/70 pb-4">
          <p class="text-[11px] text-gray-400 mb-1">Title</p>
          <p class="font-semibold text-sm text-white break-words">
            {{ props.file.name }}
          </p>

          <div class="mt-4">
            <p class="text-[11px] text-gray-400 mb-1">File Size</p>
            <p class="font-semibold text-sm text-white">
              {{ props.file.file_size_gb }}
              <template v-if="props.file.width && props.file.height">({{ props.file.width }} x {{ props.file.height }})</template>
            </p>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto scrollbar-light pr-2 -mr-2 pt-3" data-onboarding-video-snipping="clips-list">
        <!-- side panel -->
        <video-editor-clips-list @close="modal.close()"/>
      </div>

      <div v-if="clips?.length" class="bottom-0 left-0 right-0 py-4 px-0.5 bg-gray-800 grid grid-cols-2 gap-2">
        <u-button
          @click="modal.close()"
          color="charcoal"
          block
          size="xl"
        >
          Cancel
        </u-button>
        <u-button
          @click="openExportModePicker"
          data-onboarding-video-snipping="clips-save"
          block
          size="xl"
          :loading="isSubmittingExport"
          :disabled="isSubmittingExport"
          class="!text-white"
        >
          Save As
        </u-button>
      </div>
    </section>

    <file-capture-frame-preview
      v-model:frame-src="frameSrc"
      :is-portrait="props.file.height > props.file.width"
      z-index-class="z-[2100]"
    />
  </div>
</template>

<script setup>
  import {CoreModalConfirm, VideoEditorExportModeModal} from '#components';
  import {storeToRefs} from 'pinia';
  import {getFileNameWithoutExtension, getFileDurationSeconds} from '~/utils/video-editor/clip-utils.js';

  const props = defineProps({
    file: Object,
    modal: Object,
    fileContext: {
      type: Object,
      default: () => ({})
    }
  });

  provide('formatTime', function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '00:00.000';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const millis = Math.floor((seconds % 1) * 1000);

    return `${String(minutes).padStart(2, '0')}:` +
      `${String(secs).padStart(2, '0')}.` +
      `${String(millis).padStart(3, '0')}`;
  });

  const videoEditorStore = useVideoEditorStore();
  const {clips, jobStatus, isSubmittingExport} = storeToRefs(videoEditorStore);
  const {editCustomFrame: editCustomFrameAction} = useVideoEditorActions();
  const fileContext = props.fileContext;

  const {error, status} = await useAsyncData(
    `clips-for-${props.file.id}`,
    () => videoEditorStore.restoreEditingSession({fileId: props.file.id})
  );

  const isJobProcessing = computed(() => [VIDEO_EDITOR_STATUS.pending, VIDEO_EDITOR_STATUS.processing].includes(jobStatus.value));
  const isJobErrored = computed(() => jobStatus.value === VIDEO_EDITOR_STATUS.errored);

  const videoContainerEl = ref();
  const videoPlayerComp = ref();
  const isPanelVisible = ref(Boolean(clips.value?.length));
  const isInfoSectionVisible = ref(true);
  const isPlaybackErrored = ref(false);
  const player = ref(null);
  const { frameSrc, captureVideoFrame: _captureVideoFrame } = useCaptureVideoFrame({
    file: toRef(props, 'file'),
    fileContext
  });
  const statusInfoModal = ref(null);
  const displayedCurrentTimeSeconds = ref(0);

  const {width: containerWidth, height: containerHeight} = useElementSize(videoContainerEl);
  const disableClipEditing = ref(false);

  const featurePosition = useDetailFeaturePosition({
    featureDimensions: computed(() => ({
      width: Math.max(props.file.width, VIDEO_MIN_WIDTH),
      height: Math.max(props.file.height, VIDEO_MIN_HEIGHT)
    })),
    availableSpace: computed(() => ({
      width: containerWidth.value,
      height: containerHeight.value
    }))
  });

  function togglePanel() {
    isPanelVisible.value = !isPanelVisible.value;
  }

  function toggleInfoSection() {
    isInfoSectionVisible.value = !isInfoSectionVisible.value;
  }

  const videoSrc = computed(() => props.file.preview_url || props.file.download_items[0].path);
  const initialTimelineDurationSeconds = computed(() => getFileDurationSeconds(props.file));

  function closeStatusInfoModal() {
    if (!process.client) {
      return;
    }

    statusInfoModal.value?.close();
    statusInfoModal.value = null;
  }

  function openStatusInfoModal({heading, message, confirmText = 'OK', color = 'primary'}) {
    if (!process.client || statusInfoModal.value) {
      return;
    }

    const modalId = `video-editor-processing-info-${props.file.id}`;
    statusInfoModal.value = useFModal({
      id: modalId,
      size: 'sm',
      showClose: false,
      clickToClose: false,
      escToClose: false,
      component: CoreModalConfirm,
      props: {
        heading,
        message,
        confirmText,
        color,
        nonCancelable: true,
        confirmMethod: async () => {
          await props.modal.close();
        }
      },
      onClose() {
        statusInfoModal.value = null;
      }
    });

    statusInfoModal.value.open();
  }

  function onVideoError() {
    isPlaybackErrored.value = true;
  }

  function setDisplayedCurrentTime(nextTime) {
    const normalizedTime = Number(nextTime);
    displayedCurrentTimeSeconds.value = Number.isFinite(normalizedTime)
      ? Math.max(0, normalizedTime)
      : 0;
  }

  function syncDisplayedCurrentTimeFromPlayer() {
    setDisplayedCurrentTime(player.value?.currentTime?.() || 0);
  }

  async function captureVideoFrame() {
    const video = videoPlayerComp.value?.videoEl;
    if (!video) {
      return;
    }

    useGtm().trackEvent({
      event: 'core_ui-video_editor-capture_frame_btn',
      action: 'click',
      category: 'video_editor'
    });

    await _captureVideoFrame(video);
  }

  async function openExportModePicker() {
    if (isSubmittingExport.value) {
      return;
    }

    useGtm().trackEvent({
      event: 'core_ui-video_editor-export_btn',
      action: 'click',
      category: 'video_editor'
    });

    if ((clips.value?.length || 0) <= 1) {
      await saveClips({mode: VIDEO_EDITOR_CLIP_MODE.clip});
      return;
    }

    useFModal({
      id: 'video-editor-export-mode',
      size: 'md',
      component: VideoEditorExportModeModal,
      eventHandlers: {
        async 'make-clips'() {
          await saveClips({mode: VIDEO_EDITOR_CLIP_MODE.clip});
        },
        async 'cut-regions'() {
          await saveClips({mode: VIDEO_EDITOR_CLIP_MODE.cut});
        }
      }
    }).open();
  }

  async function createTitleCoverClip() {
    useGtm().trackEvent({
      event: 'core_ui-video_editor-create_title_cover_btn',
      action: 'click',
      category: 'video_editor'
    });

    const duration = Number(player.value?.duration?.()) || Number(initialTimelineDurationSeconds.value) || 0;
    if (!duration || Number.isNaN(duration)) {
      useErrorToast().add({
        description: 'Video is still loading. Please try again in a moment.'
      });
      return;
    }

    const newClip = videoEditorStore.createClip({
      startTime: 0,
      endTime: duration,
      name: getFileNameWithoutExtension(props.file)
    });

    if (!newClip) {
      return;
    }

    await editCustomFrameAction({
      clip: newClip,
      frame: newClip.frames?.title || null
    });
  }

  const CONTEXT_TYPES_WITH_ID = new Set([FILE_CONTEXTS.album, FILE_CONTEXTS.tag, FILE_CONTEXTS.person]);

  async function saveClips({mode = VIDEO_EDITOR_CLIP_MODE.clip} = {}) {
    if (isSubmittingExport.value) {
      return;
    }

    const contextType = fileContext.contextType;
    const contextId = CONTEXT_TYPES_WITH_ID.has(contextType) ? fileContext.contextId : undefined;
    const durationSeconds = Number(player.value?.duration?.()) || Number(initialTimelineDurationSeconds.value) || 0;

    try {
      await videoEditorStore.submit({mode, contextType, contextId, durationSeconds});
      openStatusInfoModal({
        heading: 'Your Project Is Being Prepared',
        message: `We are processing your media in the background. You can safely leave this page - we'll notify you when it's ready.` ,
        confirmText: 'Got It',
        color: 'primary'
      });
    } catch (e) {
      if (e?.message === VIDEO_EDITOR_TITLE_FRAME_EXPORT_ERROR) {
        useErrorToast().add({
          title: 'Title Cover Export Incomplete',
          description: 'One or more title covers could not be exported. Please try submitting again.'
        });
      } else if (e?.message === VIDEO_EDITOR_CLIP_TOO_SHORT_ERROR) {
        const clipNames = (e?.details?.clipNames || []).filter(Boolean);
        const clipList = clipNames.length ? clipNames.join(', ') : 'One or more clips';
        const tooShortModal = useConfirmModal({
          id: 'video-editor-clip-too-short',
          heading: 'Clip Too Short',
          message: `${clipList} ${clipNames.length === 1 ? 'is' : 'are'} shorter than the minimum ${VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS} second clip length. Please adjust the clip before exporting.`,
          confirmText: 'Ok',
          nonCancelable: true,
          confirmMethod: () => tooShortModal.close()
        });
        tooShortModal.open();
      } else if (e?.message === VIDEO_EDITOR_CLIP_OUT_OF_BOUNDS_ERROR) {
        const clipNames = (e?.details?.clipNames || []).filter(Boolean);
        const clipList = clipNames.length ? clipNames.join(', ') : 'One or more clips';
        const outOfBoundsModal = useConfirmModal({
          id: 'video-editor-clip-out-of-bounds',
          heading: 'Clip Extends Past Video',
          message: `${clipList} ${clipNames.length === 1 ? 'extends' : 'extend'} past the end of the video. Please adjust the clip before exporting.`,
          confirmText: 'Ok',
          nonCancelable: true,
          confirmMethod: () => outOfBoundsModal.close()
        });
        outOfBoundsModal.open();
      } else {
        useErrorToast().add();
      }

      throw e;
    }
  }

  watch([isJobProcessing, isJobErrored], ([isProcessing, isErrored]) => {
    if (isProcessing) {
      openStatusInfoModal({
        heading: 'Save In Progress',
        message: 'We are working on your media. We will have it ready for you shortly!',
        confirmText: `I'll Come Back`,
        color: 'primary'
      });
      return;
    }

    if (isErrored) {
      openStatusInfoModal({
        heading: 'Save Error',
        message: 'We encountered an error while processing your media and are looking into it. Please check back a little later.',
        confirmText: 'OK',
        color: 'red'
      });
      return;
    }

    closeStatusInfoModal();
  },
  {
    immediate: true
  });

  watch(() => clips.value?.length || 0, (nextCount, previousCount) => {
    if (previousCount === 0 && nextCount > 0) {
      isPanelVisible.value = true;
    }
  });

  onMounted(async () => {
    const playerAvailableWatch = watch(() => videoPlayerComp.value.player, async (newVal) => {
      if (newVal) {
        player.value = newVal;
        setDisplayedCurrentTime(newVal.currentTime?.() || 0);
        newVal.on('timeupdate', syncDisplayedCurrentTimeFromPlayer);
        newVal.on('loadedmetadata', syncDisplayedCurrentTimeFromPlayer);
        newVal.on('durationchange', syncDisplayedCurrentTimeFromPlayer);
        newVal.on('seeked', syncDisplayedCurrentTimeFromPlayer);
        await nextTick();
        playerAvailableWatch();
      }
    },
    {
      immediate: true
    }
    );

    videoEditorStore.initialize({
      file: props.file,
      videoElement: videoPlayerComp.value.videoEl,
      videoSrc: videoSrc.value
    });
  });

  onUnmounted(() => {
    if (player.value) {
      player.value.off('timeupdate', syncDisplayedCurrentTimeFromPlayer);
      player.value.off('loadedmetadata', syncDisplayedCurrentTimeFromPlayer);
      player.value.off('durationchange', syncDisplayedCurrentTimeFromPlayer);
      player.value.off('seeked', syncDisplayedCurrentTimeFromPlayer);
    }

    closeStatusInfoModal();
    videoEditorStore.clear();
  });

</script>
